import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { ProductPlaceholder } from "@/components/ProductPlaceholder";
import { CloudImage } from "@/components/CloudImage";
import { CLD_MAIN, CLD_THUMB, CLD_FULL } from "@/lib/variants";
import { cn } from "@/lib/utils";

// Riquadro quadrato: la foto sta direttamente sullo sfondo #0A0A0B (nessun
// riquadro grigio), centrata, non deformata, ~80% del riquadro con margine
// uniforme per tutti i prodotti grazie alla trasformazione Cloudinary e_trim/c_pad.
const FRAME = "relative aspect-square w-full overflow-hidden rounded-2xl bg-transparent";
const IMG = "h-full w-full object-contain p-[10%]";

function Placeholder({ name }) {
  return (
    <ProductPlaceholder
      label={name}
      size="lg"
      className="h-full w-full rounded-2xl"
      data-testid="pdp-gallery-placeholder"
    />
  );
}

// Immagine principale statica ad alta qualità. Click/tap apre il visore a
// schermo intero (nessuno zoom).
function MainImage({ photo, name, onOpen }) {
  if (!photo) return <Placeholder name={name} />;

  return (
    <div
      className={cn(FRAME, "cursor-pointer")}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      aria-label="Apri foto a schermo intero"
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
      data-testid="pdp-main-frame"
    >
      <CloudImage url={photo.url} transform={CLD_MAIN} alt={photo.alt || name} className={IMG} />
    </div>
  );
}

// Visore a schermo intero ad alta risoluzione, con navigazione (nessuno zoom).
function Lightbox({ photos, index, name, onClose, onIndex }) {
  const photo = photos[index];

  const go = useCallback(
    (dir) => {
      const n = (index + dir + photos.length) % photos.length;
      onIndex(n);
    },
    [index, photos.length, onIndex]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [go, onClose]);

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[200] flex flex-col bg-ink"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      data-testid="pdp-lightbox"
    >
      <div className="flex items-center justify-between px-4 py-4 sm:px-6">
        <span className="text-sm text-muted-foreground">
          {index + 1} / {photos.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-border p-2 text-foreground transition-colors duration-200 hover:border-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime"
          aria-label="Chiudi"
          data-testid="pdp-lightbox-close"
        >
          <X className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4 pb-4 sm:px-16">
        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              className="absolute left-3 z-10 hidden rounded-full border border-border bg-ink/60 p-3 text-foreground backdrop-blur transition-colors duration-200 hover:border-foreground/50 sm:block"
              aria-label="Precedente"
              data-testid="pdp-lightbox-prev"
            >
              <ChevronLeft className="h-6 w-6" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              className="absolute right-3 z-10 hidden rounded-full border border-border bg-ink/60 p-3 text-foreground backdrop-blur transition-colors duration-200 hover:border-foreground/50 sm:block"
              aria-label="Successiva"
              data-testid="pdp-lightbox-next"
            >
              <ChevronRight className="h-6 w-6" strokeWidth={1.75} />
            </button>
          </>
        )}

        <div className="relative flex h-full w-full max-w-3xl items-center justify-center" data-testid="pdp-lightbox-stage">
          <CloudImage
            key={photo?.id}
            url={photo?.url}
            transform={CLD_FULL}
            alt={photo?.alt || name}
            draggable={false}
            className="h-full w-full object-contain"
            data-testid="pdp-lightbox-image"
          />
        </div>
      </div>

      {photos.length > 1 && (
        <div className="flex items-center justify-center gap-2 pb-6" role="tablist" aria-label="Foto">
          {photos.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onIndex(i)}
              aria-selected={i === index}
              aria-label={`Foto ${i + 1}`}
              className={cn(
                "h-2 rounded-full transition-all duration-200",
                i === index ? "w-6 bg-foreground" : "w-2 bg-muted-foreground/40 hover:bg-muted-foreground"
              )}
            />
          ))}
        </div>
      )}
    </motion.div>,
    document.body
  );
}

export function ProductGallery({ photos = [], name = "Prodotto" }) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const scrollerRef = useRef(null);
  const sig = useMemo(() => photos.map((p) => p.id).join("|"), [photos]);

  // Cambiando colore le foto cambiano: torna alla prima, senza ricaricare.
  useEffect(() => {
    setActive(0);
    if (scrollerRef.current) scrollerRef.current.scrollTo({ left: 0 });
  }, [sig]);

  const has = photos.length > 0;
  const current = has ? photos[Math.min(active, photos.length - 1)] : null;

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== active) setActive(i);
  };

  const scrollToIndex = (i) => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div data-testid="pdp-gallery" className="group/frame">
      {/* Desktop / tablet: immagine grande con zoom + miniature */}
      <div className="hidden md:block">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={current ? current.id : "placeholder"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <MainImage photo={current} name={name} onOpen={() => has && setLightbox(true)} />
          </motion.div>
        </AnimatePresence>

        {has && photos.length > 1 && (
          <div className="mt-4 grid grid-cols-5 gap-3" role="tablist" aria-label="Immagini prodotto">
            {photos.slice(0, 10).map((p, i) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={active === i}
                onClick={() => setActive(i)}
                className={cn(
                  FRAME,
                  "cursor-pointer border transition-[border-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  active === i ? "border-foreground" : "border-border hover:border-foreground/40"
                )}
                data-testid={`pdp-thumb-${i}`}
              >
                <CloudImage url={p.url} transform={CLD_THUMB} alt={p.alt || ""} className="h-full w-full object-contain p-[12%]" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mobile 375px: carosello a scorrimento con puntini */}
      <div className="md:hidden">
        {has ? (
          <>
            <div
              ref={scrollerRef}
              onScroll={onScroll}
              className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="pdp-mobile-scroller"
            >
              {photos.map((p) => (
                <div key={p.id} className="w-full shrink-0 snap-center" onClick={() => setLightbox(true)}>
                  <div className={FRAME}>
                    <CloudImage url={p.url} transform={CLD_MAIN} alt={p.alt || name} className={IMG} />
                  </div>
                </div>
              ))}
            </div>
            {photos.length > 1 && (
              <div className="mt-4 flex items-center justify-center gap-2" role="tablist" aria-label="Foto">
                {photos.map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => scrollToIndex(i)}
                    aria-selected={active === i}
                    aria-label={`Foto ${i + 1}`}
                    className={cn(
                      "h-2 rounded-full transition-all duration-200",
                      active === i ? "w-6 bg-foreground" : "w-2 bg-muted-foreground/40"
                    )}
                    data-testid={`pdp-dot-${i}`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className={FRAME}>
            <Placeholder name={name} />
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && has && (
          <Lightbox
            photos={photos}
            index={Math.min(active, photos.length - 1)}
            name={name}
            onClose={() => setLightbox(false)}
            onIndex={(i) => setActive(i)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
